"use client";

import { NumericFormat } from "react-number-format";
import { useParcelas } from "./_hook";
import { usePrenotaStore } from "@PN/inclusao";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
  fmt,
  parse,
  Label,
  Button,
  Input,
  DatePicker,
  cn,
} from "visuais";
import { ParcelasStatusInfo } from "./ParcelasStatusInfo";
import { SimpleAuthForm } from "./SimpleAuthForm";
import { useMemo, useState, useEffect } from "react";
import { useCatalogs } from "store";
import { IconRefresh } from "@tabler/icons-react";
import { Plus, Trash2 } from "lucide-react";

export function ParcelasTable() {
  const {
    parcelas,
    isLoading,
    error,
    areParcelasSaved,
    handleVencimentoChange,
    handleValorChange,
    handleAddParcela,
    handleRemoveParcela,
    handleForceRefetch,
    valorNF,
    refetch,
  } = useParcelas();

  const pagamentos = usePrenotaStore((state) => state.draft.pagamentos);
  const documento = usePrenotaStore((state) => state.draft.documento);
  const { condicoes } = useCatalogs();
  const condicaoRow = pagamentos.condicaoFinanceira
    ? condicoes.getBy("E4_CODIGO", pagamentos.condicaoFinanceira)
    : undefined;
  const isEditable = condicaoRow?.E4_TIPO === "9";

  // C35 só vale para notas com data de emissão anterior a 01/02/2026
  const limiteEmissaoC35 = useMemo(() => {
    const d = new Date(2026, 1, 1);
    d.setHours(0, 0, 0, 0);
    return d.getTime();
  }, []);
  const dataEmissaoMs = useMemo(() => {
    const raw = documento?.dataEmissao;
    if (!raw || typeof raw !== "string") return null;
    const isoMatch = raw.match(/^(\d{4})-(\d{2})-(\d{2})/);
    if (isoMatch) {
      const d = new Date(parseInt(isoMatch[1], 10), parseInt(isoMatch[2], 10) - 1, parseInt(isoMatch[3], 10));
      d.setHours(0, 0, 0, 0);
      return d.getTime();
    }
    const brMatch = raw.match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})$/);
    if (brMatch) {
      const d = new Date(parseInt(brMatch[3], 10), parseInt(brMatch[2], 10) - 1, parseInt(brMatch[1], 10));
      d.setHours(0, 0, 0, 0);
      return d.getTime();
    }
    return null;
  }, [documento?.dataEmissao]);
  const isC35ComEmissaoInvalida =
    (pagamentos.condicaoFinanceira ?? "") === "C35" &&
    dataEmissaoMs !== null &&
    dataEmissaoMs >= limiteEmissaoC35;

  // Cálculo do total das parcelas
  const totalParcelas = parcelas.reduce((sum, p) => sum + (p.valor || 0), 0);
  const isValorTotalValido =
    valorNF === 0 || Math.abs(totalParcelas - valorNF) < 0.01;

  // Estado para mostrar feedback de salvamento automático
  const [showAutoSaveFeedback, setShowAutoSaveFeedback] = useState(false);

  // Estado para controlar se deve mostrar o formulário de login
  const [showAuthForm, setShowAuthForm] = useState(false);

  // Mostra feedback quando parcelas são salvas automaticamente
  useEffect(() => {
    if (parcelas.length > 0 && areParcelasSaved) {
      setShowAutoSaveFeedback(true);
      const timer = setTimeout(() => setShowAutoSaveFeedback(false), 2000);
      return () => clearTimeout(timer);
    }
  }, [parcelas.length, areParcelasSaved]);

  // Detecta erro 401 e mostra formulário de login
  useEffect(() => {
    if (error && "response" in error && error.response?.status === 401) {
      setShowAuthForm(true);
    } else {
      setShowAuthForm(false);
    }
  }, [error]);

  // Memoiza os objetos Date para cada parcela
  const vencDates = useMemo(
    () =>
      parcelas.map((parcela) => {
        if (
          parcela.vencimento &&
          /^\d{4}-\d{2}-\d{2}$/.test(parcela.vencimento)
        ) {
          // ✅ CORRIGIDO: Usa parse consistente para evitar problemas de timezone
          return parse.dates.ISO(parcela.vencimento, { allowNull: true });
        }
        return parse.dates.ISO(parcela.vencimento, { allowNull: true });
      }),
    [parcelas]
  );

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Label className="text-sm font-medium text-foreground">
            Parcelas
          </Label>
          <ParcelasStatusInfo />
  
        </div>
        {/* Mostrar total das parcelas e botão de refresh */}
        {parcelas.length > 0 && (
          <div className="flex items-center gap-2">
            <div className="text-xs text-muted-foreground">
              Total: {fmt.numbers.currency(totalParcelas)}
            </div>
            <Button
              onClick={handleForceRefetch}
              size="sm"
              variant="secondary"
              className="group size-5 hover:size-6 bg-cyan-200/70 hover:bg-cyan-200/80 dark:bg-cyan-700/70 dark:hover:bg-cyan-700/80 transition-all duration-300"
              title="Atualizar parcelas da API"
            >
              <IconRefresh className="size-4 group-hover:size-5 transition-all duration-300" />
            </Button>
          </div>
        )}
      </div>

      {/* Mostrar erro se houver */}
      {error && !showAuthForm && (
        <div className="text-xs text-destructive p-3 bg-destructive/10 dark:bg-destructive/20 rounded-md border border-destructive/20 dark:border-destructive/30">
          <div className="font-medium mb-1">Erro ao carregar parcelas:</div>
          <div className="text-muted-foreground">
            {error instanceof Error ? error.message : String(error)}
          </div>
          <Button onClick={() => refetch()} size="sm" className="mt-2">
            Tentar novamente
          </Button>
        </div>
      )}

      {/* Mostrar formulário de login para erro 401 */}
      {showAuthForm && (
        <SimpleAuthForm
          onSuccess={() => {
            setShowAuthForm(false);
            refetch(); // Tenta buscar as parcelas novamente após login
          }}
          onCancel={() => {
            setShowAuthForm(false);
          }}
        />
      )}

      <div className="space-y-3">
        {/* Tabela com shadcn */}
        <div className="border rounded-lg overflow-hidden">
          <div className="max-h-[280px] overflow-y-auto">
            <Table>
              <TableHeader className="sticky top-0 bg-background z-10">
                <TableRow className="bg-muted/50">
                  <TableHead className="text-center w-20 text-xs font-medium">
                    Parc.
                  </TableHead>
                  <TableHead className="w-40 text-xs font-medium">
                    Vencimento
                  </TableHead>
                  <TableHead className="text-right text-xs font-medium">
                    Valor
                  </TableHead>
                  {isEditable && (
                    <TableHead className="w-10 text-xs font-medium" />
                  )}
                </TableRow>
              </TableHeader>
              <TableBody>
                {parcelas.length > 0 ? (
                  parcelas.map((parcela, idx) => {
                    const vencDate = vencDates[idx];
                    const hasVencimentoError = !parcela.vencimento;
                    return (
                      <TableRow
                        key={parcela.item || idx}
                        className="hover:bg-muted/30"
                      >
                        <TableCell className="text-center w-20 py-2">
                          <Input
                            value={parcela.item}
                            disabled
                            className="h-8 text-xs text-center border-0 bg-transparent font-medium"
                          />
                        </TableCell>
                        <TableCell className="w-40 py-2">
                          <DatePicker
                            value={vencDate || undefined}
                            placeholder="dd/mm/aaaa"  
                            disabled={!isEditable}
                            className={cn(
                              "h-8 text-xs",
                                hasVencimentoError
                                  ? "border-destructive focus:border-destructive"
                                  : ""
                            )}
                            onChange={(date) => {
                              if (date && "toDateString" in date) {
                                handleVencimentoChange(idx, date as Date);
                              }
                            }}
                            allowTyping={true}
                          />
                        </TableCell>
                        <TableCell className="py-2">
                          <NumericFormat
                            value={parcela.valor}
                            thousandSeparator="."
                            decimalSeparator=","
                            decimalScale={2}
                            fixedDecimalScale
                            prefix="R$ "
                            customInput={Input}
                            disabled={!isEditable}
                            onValueChange={({ floatValue }) => {
                              handleValorChange(idx, floatValue ?? 0);
                              // Feedback visual temporário
                              const element = document.querySelector(
                                `[data-parcela-valor="${idx}"]`
                              );
                              if (element) {
                                element.classList.add(
                                  "bg-cyan-50",
                                  "dark:bg-cyan-900/30",
                                  "border-cyan-300",
                                  "dark:border-cyan-600"
                                );
                                setTimeout(() => {
                                  element.classList.remove(
                                    "bg-cyan-50",
                                    "dark:bg-cyan-900/30",
                                    "border-cyan-300",
                                    "dark:border-cyan-600"
                                  );
                                }, 500);
                              }
                            }}
                            allowNegative={false}
                            className="h-8 text-xs text-right transition-all duration-200 hover:border-primary/50 focus:border-primary"
                            data-parcela-valor={idx}
                          />
                        </TableCell>
                        {isEditable && (
                          <TableCell className="py-2 w-10">
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handleRemoveParcela(idx)}
                              disabled={parcelas.length <= 1}
                              className="h-7 w-7 p-0 text-destructive hover:bg-destructive/10 disabled:opacity-30"
                              title="Remover parcela"
                            >
                              <Trash2 className="h-3.5 w-3.5" />
                            </Button>
                          </TableCell>
                        )}
                      </TableRow>
                    );
                  })
                ) : (
                  <TableRow>
                    <TableCell
                      colSpan={3}
                      className="text-center py-8 text-muted-foreground text-sm"
                    >
                      {isLoading ? (
                        <div className="flex items-center justify-center gap-2">
                          <div className="w-4 h-4 border-2 border-cyan-500 border-t-transparent rounded-full animate-spin"></div>
                          <span>Carregando parcelas...</span>
                        </div>
                      ) : (
                        "Nenhuma parcela encontrada"
                      )}
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </div>
        </div>

        {isEditable && (
          <Button
            variant="outline"
            size="sm"
            onClick={handleAddParcela}
            className="w-full text-xs"
          >
            <Plus className="h-3.5 w-3.5 mr-1" />
            Adicionar parcela
          </Button>
        )}

        {/* Validação do total */}
        {parcelas.length > 0 && !isValorTotalValido && valorNF !== null && (
          <div className="text-xs text-destructive p-3 bg-destructive/10 dark:bg-destructive/20 rounded-md border border-destructive/20 dark:border-destructive/30">
            <div className="font-medium mb-1">Divergência de valores:</div>
            <div className="space-y-1">
              <div>
                Total das parcelas: {fmt.numbers.currency(totalParcelas)}
              </div>
              <div>Valor da NF: {fmt.numbers.currency(valorNF)}</div>
            </div>
          </div>
        )}

        {/* C35 só permitida para emissão anterior a 01/02/2026 */}
        {isC35ComEmissaoInvalida && (
          <div className="text-xs text-destructive p-3 bg-destructive/10 dark:bg-destructive/20 rounded-md border border-destructive/20 dark:border-destructive/30">
            <div className="font-medium">
              Condição C35 só é permitida para notas com data de emissão anterior a 01/02/2026.
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
